export interface TreeStrategy<Item> {
    /**
     * Get the unique identifier for a tree item.
     */
    getId(item: Item): string;

    /**
     * Get the descendants of a tree item.
     */
    getChildren(item: Item): Item[];

    /**
     * Set the descendants of a tree item.
     */
    setChildren(item: Item, children: Item[]): Item;

    /**
     * Compare two items at a shallow level (without comparing descendants).
     * Should return 0 if the items are equal.
     */
    compareItems(a: Item, b: Item): number;
}

type TreeIndex<Item> = Map<string, { item: Item; parent: string | undefined }>;

/**
 * Index all nodes of a tree by their identifier.
 */
export function indexTree<Item>(
    tree: Item[],
    strategy: TreeStrategy<Item>,
    parent: string | undefined = undefined,
    index: TreeIndex<Item> = new Map()
): TreeIndex<Item> {
    for (const item of tree) {
        const id = strategy.getId(item);
        index.set(id, { item, parent });
        indexTree(strategy.getChildren(item), strategy, id, index);
    }

    return index;
}

/**
 * Lookup the descendant of a node in a tree index.
 */
export function lookupTreeDescendants<Item>(
    strategy: TreeStrategy<Item>,
    index: TreeIndex<Item>,
    parent: string
): null | Item[] {
    const node = index.get(parent);
    if (!node) {
        return null;
    }

    return strategy.getChildren(node.item);
}
